import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, AlertDescription, Button, ConfirmDialog, Text } from '@shared/ui';
import type { useArticleDeletion } from '../model/use-article-deletion';
import { articleErrorKey } from '../model/presentation';

export function ArticleDeletionFeedback({
  deletion,
  canDelete,
  onDismiss,
}: {
  deletion: ReturnType<typeof useArticleDeletion>;
  canDelete: boolean;
  onDismiss?: () => void;
}) {
  const { t } = useTranslation();
  const {
    deleted,
    deletionState,
    monitor,
    error,
    unresolvedDelete,
    pending,
    refresh,
    remove,
    blocker,
    confirm,
    denied,
    closeConfirmation,
  } = deletion;
  if (deleted) return null;
  const recoveryTitle = unresolvedDelete
    ? 'articles.deletion.unknown'
    : monitor.error
      ? 'articles.deletion.monitorUnavailable'
      : deletionState === 'failed'
        ? 'articles.deletion.failed'
        : error
          ? articleErrorKey(error)
          : undefined;
  const recoveryDescription = unresolvedDelete
    ? 'articles.deletion.sameRetry'
    : monitor.error
      ? 'articles.deletion.monitorDescription'
      : deletionState === 'failed'
        ? 'articles.deletion.failedDescription'
        : undefined;
  return (
    <>
      {recoveryTitle ? (
        <Alert
          variant={
            deletionState === 'failed' || (error && !unresolvedDelete) ? 'destructive' : 'default'
          }
        >
          <AlertTitle>{t(recoveryTitle)}</AlertTitle>
          <AlertDescription className="space-y-2">
            {recoveryDescription ? <Text size="sm">{t(recoveryDescription)}</Text> : null}
            <div className="flex flex-wrap gap-2">
              {unresolvedDelete ? (
                canDelete ? (
                  <Button
                    variant="outline"
                    size="sm"
                    loading={pending}
                    onClick={() => {
                      void remove();
                    }}
                  >
                    {t('articles.deletion.retrySame')}
                  </Button>
                ) : null
              ) : (
                <Button variant="outline" size="sm" onClick={refresh}>
                  {t('articles.refresh')}
                </Button>
              )}
              {onDismiss ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pending || unresolvedDelete}
                  onClick={onDismiss}
                >
                  {t('common.close')}
                </Button>
              ) : null}
            </div>
          </AlertDescription>
        </Alert>
      ) : null}
      <ConfirmDialog
        open={blocker.status === 'blocked'}
        title={t('articles.deletion.unknown')}
        description={t('articles.deletion.sameRetry')}
        confirmLabel={t('articles.discard.confirm')}
        cancelLabel={t('common.cancel')}
        confirmButtonProps={{ disabled: true }}
        onConfirm={() => undefined}
        onOpenChange={(open) => {
          if (!open && blocker.status === 'blocked') blocker.reset();
        }}
      />
      <ConfirmDialog
        open={confirm && canDelete && !denied}
        onOpenChange={closeConfirmation}
        title={t('articles.deletion.confirmTitle')}
        description={t('articles.deletion.confirmDescription')}
        confirmLabel={t('articles.deletion.confirm')}
        cancelLabel={t('common.cancel')}
        confirmPending={pending}
        closeOnConfirm={false}
        onConfirm={() => {
          void remove();
        }}
      />
    </>
  );
}
